import { GraduationCap, Award, HeartHandshake, CheckCircle2 } from "lucide-react";
import { DOCTORS, FAQS } from "@/data/site";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import EditorialMarquee from "@/components/EditorialMarquee";

const D = DOCTORS.pallavi;

export default function About() {
  return (
    <>
      <PageHero
        crumb="About"
        eyebrow="About Dr. Pallavi Mishra"
        titleLines={["Meet Dr. Pallavi Mishra —", "Your Trusted Cardiologist", "in Varanasi"]}
        subtitle="A KGMU-trained Consultant Cardiologist devoted to preventive, non-invasive and deeply personalized heart care."
      />

      {/* Profile split */}
      <section className="pb-24 md:pb-32 bg-paper" data-testid="about-profile">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-line bg-mint group">
                <div className="absolute inset-0 z-10 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(46,139,62,0.14),transparent_60%)]" />
                <img
                  src={D.photo}
                  alt="Dr. Pallavi Mishra"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="mt-6 rounded-2xl border border-line bg-surface p-6">
                <h3 className="font-display text-2xl text-ink">{D.name}</h3>
                <p className="text-sm text-brand font-semibold mt-1">{D.role}</p>
                <p className="text-sm text-sage">{D.subrole}</p>
                <div className="mt-4 pt-4 border-t border-line flex items-baseline gap-2">
                  <span className="font-display text-3xl text-brand leading-none">{D.experience}</span>
                  <span className="text-sm text-sage font-medium">Years of Expertise</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Biography</p>
              <div className="space-y-6 text-lg text-sage leading-relaxed">
                <p>
                  Dr. Pallavi Mishra is a Consultant Cardiologist in Varanasi whose practice is built around a simple conviction: the most powerful heart care happens <span className="text-ink font-medium">before</span> disease takes hold. Trained at King George&rsquo;s Medical University (KGMU), she brings deep clinical rigour to preventive and non-invasive cardiology.
                </p>
                <p>
                  As a Diabeto-Cardiologist, she specialises in the intersections that matter most — how diabetes, blood pressure and lifestyle shape the heart — and as an Echocardiography Specialist she reads the heart with precision and care. With <span className="text-ink font-medium">15+ years of expertise</span>, she brings depth and reassurance to every consultation.
                </p>
                <p>
                  Beyond her clinic, Dr. Mishra serves as <span className="text-ink font-medium">National Chairman, Cardiabcon</span> and as Assistant Professor at PIIRE, Varanasi — roles that keep her at the forefront of preventive cardiac medicine.
                </p>
                <p>
                  Alongside cardiology, Dr. Mishra also provides <span className="text-ink font-medium">General Physician care</span> for everyday concerns — fever, seasonal illness, diabetes, blood pressure, thyroid and preventive check-ups — so patients often find one trusted doctor for their whole family's routine health.
                </p>
              </div>
            </Reveal>

            {/* Philosophy */}
            <Reveal delay={0.05}>
              <div className="mt-14 rounded-3xl bg-brand-deep text-white p-8 md:p-10 grain relative overflow-hidden">
                <div className="relative">
                  <HeartHandshake size={30} className="text-brand mb-5" />
                  <h3 className="font-display text-3xl md:text-4xl font-light leading-tight mb-4">Philosophy of Care</h3>
                  <p className="text-mint/85 text-lg leading-relaxed max-w-2xl">
                    Patient education and empowerment sit at the heart of every consultation. Dr. Mishra believes in a holistic, compassionate and personalized approach — treating the person in front of her, never just a set of numbers.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Expertise */}
            <div className="mt-14">
              <Reveal><p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-6">Areas of Expertise</p></Reveal>
              <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {D.expertise.map((e) => (
                  <StaggerItem key={e}>
                    <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-5 py-4">
                      <CheckCircle2 size={18} className="text-brand shrink-0" />
                      <span className="text-ink font-medium">{e}</span>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerGroup>
            </div>

            {/* Credentials */}
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Reveal>
                <div className="rounded-3xl border border-line bg-surface p-8 h-full">
                  <GraduationCap size={26} className="text-brand mb-5" />
                  <h4 className="font-display text-2xl text-ink mb-4">Education</h4>
                  <ul className="space-y-3">
                    {D.degrees.map((d) => (
                      <li key={d} className="text-sage text-sm leading-relaxed border-b border-line pb-3 last:border-0">{d}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="rounded-3xl border border-line bg-surface p-8 h-full">
                  <Award size={26} className="text-brand mb-5" />
                  <h4 className="font-display text-2xl text-ink mb-4">Positions & Memberships</h4>
                  <ul className="space-y-3">
                    {[...D.positions, ...D.memberships].map((d) => (
                      <li key={d} className="text-sage text-sm leading-relaxed border-b border-line pb-3 last:border-0">{d}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee dark items={["Preventive Cardiology", "Diabeto-Cardiology", "Women's Heart Health", "Echocardiography", "Non-Invasive Care"]} />

      <FAQSection items={FAQS.about} heading="About Dr. Mishra — Your Questions" />
      <CTASection title="Book Your Consultation with Dr. Pallavi Mishra" />
    </>
  );
}
